import {
    ArrayModel,
    ArrayModelFromJson,
    assignModelOptions,
    ColumnModel,
    CompositeModel,
    CustomInjector,
    JsonArrayModel,
    TableModel,
    TableModelOptions,
} from '@evorait/pace-core';
import { OdataPISparePartModel, PISparePartModel, PISparePartModelFromJson } from './pi-spare-part.model';
import { TranslateService } from '@ngx-translate/core';

export class PISparePartArrayModel extends CompositeModel {
    public materials: ArrayModel<PISparePartModel>;
    public tableModel: TableModel<PISparePartModel>;

    public constructor(data: OdataPISparePartModel[]) {
        super('PISparePartArrayModel', {});
        const injector = CustomInjector.getInjector();
        const translate = injector.get(TranslateService);

        this.materials = <ArrayModel<PISparePartModel>>this.set(new ArrayModel<PISparePartModel>('SparePartsSet'));
        this.materials.addModels([...data.map((material) => new PISparePartModel(material))]);

        const columns: ColumnModel[] = [];
        columns.push(
            new ColumnModel(<string>translate.instant('PHYSICAL_INVENTORY_DESCRIPTION_LABEL'), 'description.value'),
        );
        columns.push(
            new ColumnModel(<string>translate.instant('PHYSICAL_INVENTORY_MATERIAL_NO_LABEL'), 'material.value'),
        );
        const tableOptions = new TableModelOptions({ columns, sortColumn: 'pos' });

        this.tableModel = new TableModel<PISparePartModel>(this.materials.value, tableOptions);
    }

    public cloneSparePart(material: PISparePartModel): PISparePartModel {
        const newModel = new PISparePartModel();
        newModel.description.value = material.description.value;
        newModel.material.value = material.material.value;
        newModel.pIDocNo.value = material.pIDocNo.value;
        newModel.pIDocNo.options = { ...material.pIDocNo.options };
        newModel.isBatchNumberRequired = material.isBatchNumberRequired;
        newModel.batchNumber.value = '';
        newModel.comment.value = '';
        newModel.quantity.value = 1;
        // make new entry deletable to allow user to delete them in case of accidental addition
        newModel.isEntryDeletable = true;
        return newModel;
    }

    /**
     * This function splits materials with batch number.
     * if the quantity is greater than 1, split the material into multiple rows with quantity 1
     * Example: quantity = 3 -> 3 rows with quantity 1
     *
     * @param index index of material in the array
     */
    public splitMaterial(index: number): void {
        const originalMaterial = this.materials.getModelByIndex(index);

        if (!originalMaterial.isBatchNumberRequired) {
            return;
        }

        originalMaterial.validateFields();

        const quantity = originalMaterial.quantity.value;

        if (quantity && quantity > 1) {
            const splitMaterials = [];

            const material = this.cloneSparePart(originalMaterial);
            material.batchNumber.value = originalMaterial.batchNumber.value;
            material.comment.value = originalMaterial.comment.value;
            material.isEntryDeletable = originalMaterial.isEntryDeletable;
            material.quantity = assignModelOptions(
                material.quantity,
                {
                    ...material.quantity.options,
                    minValue: 0,
                },
                this,
            );
            splitMaterials.push(material);

            for (let i = 1; i < quantity; i++) {
                const sparePart = this.cloneSparePart(originalMaterial);
                sparePart.quantity = assignModelOptions(
                    sparePart.quantity,
                    {
                        ...sparePart.quantity.options,
                        maxValue: 1,
                    },
                    this,
                );
                splitMaterials.push(sparePart);
            }
            this.materials.replaceModelAtIndex(splitMaterials, index);
        }
    }

    public toJSON(): JsonPISparePartArrayModel {
        return {
            materials: this.materials,
        };
    }
}

export class PISparePartArrayModelFromJson extends PISparePartArrayModel {
    public constructor(data: JsonPISparePartArrayModel) {
        super([]);
        this.materials = <ArrayModel<PISparePartModel>>(
            this.set(new ArrayModelFromJson(PISparePartModelFromJson, data.materials))
        );
        this.tableModel.rows = this.materials.value;
    }
}

export interface JsonPISparePartArrayModel {
    materials: JsonArrayModel;
}
